import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import Image from 'react-bootstrap/Image';
import Cropper from 'react-easy-crop';
import { useTheme } from '../context/ThemeContext';
import StoryService from '../services/story';
import './Studio.css';

const Studio = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Add item form state
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaType, setMediaType] = useState('PHOTO');
  const [mediaPreview, setMediaPreview] = useState(null);
  const [addItemLoading, setAddItemLoading] = useState(false);
  const [addItemError, setAddItemError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  
  // We've removed the link functionality as requested
  
  // Image cropping state
  const [showCropModal, setShowCropModal] = useState(false);
  const [croppedImage, setCroppedImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Studio view state
  const [studioView, setStudioView] = useState('options'); // 'options', 'upload', 'blank'
  
  // Sidebar state
  const [activeSidebarItem, setActiveSidebarItem] = useState(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [draggedComponent, setDraggedComponent] = useState(null);

  useEffect(() => {
    fetchStory();
  }, [storyId]);
  
  // No need for cleanup since we removed link functionality

  const fetchStory = async () => {
    try {
      setLoading(true);
      const data = await StoryService.getStoryById(storyId);
      setStory(data);
      setError('');
    } catch (err) {
      setError('Failed to load story: ' + (err.response?.data || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleMediaChange = (e) => {
    let file;
    
    // Handle both drag-drop and file input
    if (e.dataTransfer) {
      file = e.dataTransfer.files[0];
    } else if (e.target.files) {
      file = e.target.files[0];
    }
    
    if (file) {
      // Check if file is an image or video based on mediaType
      if (mediaType === 'PHOTO' && !file.type.match('image.*')) {
        setAddItemError('Please select an image file');
        return;
      }
      
      if (mediaType === 'VIDEO' && !file.type.match('video.*')) {
        setAddItemError('Please select a video file');
        return;
      }
      
      // Check file size (max 60MB)
      if (file.size > 60 * 1024 * 1024) {
        setAddItemError('File size should be less than 60MB');
        return;
      }
      
      setMediaFile(file);
      
      // Create preview
      if (mediaType === 'PHOTO') {
        const reader = new FileReader();
        reader.onload = () => {
          setMediaPreview(reader.result);
          // Don't automatically show crop modal - make it optional
        };
        reader.readAsDataURL(file);
      } else {
        // For videos, just set the preview URL
        setMediaPreview(URL.createObjectURL(file));
      }
      
      setAddItemError('');
    }
  };
  
  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  // Removed link-related functions

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleMediaChange(e);
  };

  const handleAddItem = async () => {
    // Validate form
    if (!mediaFile) {
      setAddItemError('Please select a media file');
      return;
    }
    
    try {
      setAddItemLoading(true);
      
      // Prepare the file to upload
      let fileToUpload;
      
      if (croppedImage) {
        // If we have a cropped image, use its blob
        fileToUpload = croppedImage.blob;
      } else {
        // Otherwise use the original file
        fileToUpload = mediaFile;
      }
      
      // Calculate the next order number
      const nextOrder = story.items.length > 0
        ? Math.max(...story.items.map(item => item.order)) + 1
        : 1;
      
      const response = await StoryService.addStoryItem(
        storyId,
        fileToUpload,
        mediaType,
        nextOrder,
        null, // linkUrl
        null, // linkLabel
        null, // linkPositionX
        null  // linkPositionY
      );
      
      if (response && response.storyItemId) {
        // Navigate back to story detail page
        navigate(`/story/${storyId}`);
      } else {
        setAddItemError('Failed to add content. Please try again.');
      }
    } catch (err) {
      setAddItemError('Failed to add content: ' + (err.response?.data || err.message));
    } finally {
      setAddItemLoading(false);
    }
  };

  // Handle crop complete
  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    console.log('Crop complete:', croppedAreaPixels);
    setCroppedAreaPixels(croppedAreaPixels);
  };

  // Create a cropped image
  const getCroppedImg = () => {
    try {
      console.log('Getting cropped image with pixels:', croppedAreaPixels);
      
      if (!croppedAreaPixels) {
        setAddItemError('Please select a crop area first');
        return;
      }
      
      // Create a canvas element
      const canvas = document.createElement('canvas');
      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;
      
      const ctx = canvas.getContext('2d');
      
      // Create an image object
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.src = mediaPreview;
      
      // When the image is loaded, draw it on the canvas
      image.onload = () => {
        try {
          // Draw the cropped image
          ctx.drawImage(
            image,
            croppedAreaPixels.x,
            croppedAreaPixels.y,
            croppedAreaPixels.width,
            croppedAreaPixels.height,
            0,
            0,
            croppedAreaPixels.width,
            croppedAreaPixels.height
          );
          
          // Convert canvas to blob
          canvas.toBlob((blob) => {
            if (!blob) {
              console.error('Failed to create blob');
              setAddItemError('Failed to crop image: could not create blob');
              return;
            }
            
            console.log('Created blob with size:', blob.size);
            
            // Use the blob directly instead of creating a File object
            const blobUrl = URL.createObjectURL(blob);
            
            // Create a simple object to store the blob and its URL
            const croppedFileObj = {
              blob: blob,
              url: blobUrl,
              name: mediaFile.name,
              type: 'image/jpeg',
              lastModified: Date.now()
            };
            
            setCroppedImage(croppedFileObj);
            setShowCropModal(false);
          }, 'image/jpeg', 1);
        } catch (drawError) {
          console.error('Error drawing image:', drawError);
          setAddItemError('Failed to crop image: ' + drawError.message);
        }
      };
      
      // Handle image loading errors
      image.onerror = (error) => {
        console.error('Error loading image for crop:', error);
        setAddItemError('Failed to load image for cropping');
      };
    } catch (error) {
      console.error('Error in getCroppedImg:', error);
      setAddItemError('Failed to crop image: ' + error.message);
    }
  };

  // Handle option selection
  const handleOptionSelect = (option) => {
    if (option === 'image') {
      setMediaType('PHOTO');
      setStudioView('upload');
    } else if (option === 'video') {
      setMediaType('VIDEO');
      setStudioView('upload');
    } else if (option === 'blank') {
      // Handle blank story creation
      setStudioView('blank');
    }
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
        <Button variant="primary" onClick={() => navigate(`/story/${storyId}`)}>
          Back to Story
        </Button>
      </Container>
    );
  }

  if (!story) {
    return (
      <Container className="mt-5">
        <Alert variant="warning">Story not found</Alert>
        <Button variant="primary" onClick={() => navigate('/stories')}>
          Back to Stories
        </Button>
      </Container>
    );
  }

  return (
    <div className={`studio-container ${darkMode ? 'dark-theme' : ''} ${sidebarExpanded ? 'sidebar-open' : ''}`}>
      <div className="studio-header">
        <Button variant="outline-secondary" onClick={() => navigate(`/story/${storyId}`)}>
          <i className="bi bi-arrow-left"></i> Back to Story
        </Button>
        <h2>Add Content to Story</h2>
      </div>

      {addItemError && <Alert variant="danger" className="studio-alert">{addItemError}</Alert>}

      <div className="studio-content">
        {/* Left Sidebar */}
        <div className="studio-sidebar">
          <div className="sidebar-main">
            <div
              className={`sidebar-item ${activeSidebarItem === 'inspire' ? 'active' : ''}`}
              onClick={() => {
                setActiveSidebarItem('inspire');
                setSidebarExpanded(activeSidebarItem !== 'inspire' || !sidebarExpanded);
              }}
            >
              <div className="sidebar-item-icon">
                <i className="bi bi-stars"></i>
              </div>
              <div className="sidebar-item-text">AI Tools</div>
            </div>
            
            <div
              className={`sidebar-item ${activeSidebarItem === 'text' ? 'active' : ''}`}
              onClick={() => {
                setActiveSidebarItem('text');
                setSidebarExpanded(activeSidebarItem !== 'text' || !sidebarExpanded);
              }}
            >
              <div className="sidebar-item-icon">
                <i className="bi bi-fonts"></i>
              </div>
              <div className="sidebar-item-text">Text</div>
            </div>
            
            {/*<div
              className={`sidebar-item ${activeSidebarItem === 'cta' ? 'active' : ''}`}
              onClick={() => {
                setActiveSidebarItem('cta');
                setSidebarExpanded(activeSidebarItem !== 'cta' || !sidebarExpanded);
              }}
            >
              <div className="sidebar-item-icon">
                <i className="bi bi-arrow-up-circle"></i>
              </div>
              <div className="sidebar-item-text">CTA</div>
            </div>*/}
            
            <div
              className={`sidebar-item ${activeSidebarItem === 'interactive' ? 'active' : ''}`}
              onClick={() => {
                setActiveSidebarItem('interactive');
                setSidebarExpanded(activeSidebarItem !== 'interactive' || !sidebarExpanded);
              }}
            >
              <div className="sidebar-item-icon">
                <i className="bi bi-lightning-charge-fill"></i>
              </div>
              <div className="sidebar-item-text">Interactive</div>
            </div>
            
            {/*<div
              className={`sidebar-item ${activeSidebarItem === 'media' ? 'active' : ''}`}
              onClick={() => {
                setActiveSidebarItem('media');
                setSidebarExpanded(activeSidebarItem !== 'media' || !sidebarExpanded);
              }}
            >
              <div className="sidebar-item-icon">
                <i className="bi bi-image"></i>
              </div>
              <div className="sidebar-item-text">Media</div>
            </div>*/}
            
            {/*<div
              className={`sidebar-item ${activeSidebarItem === 'sticker' ? 'active' : ''}`}
              onClick={() => {
                setActiveSidebarItem('sticker');
                setSidebarExpanded(activeSidebarItem !== 'sticker' || !sidebarExpanded);
              }}
            >
              <div className="sidebar-item-icon">
                <i className="bi bi-palette"></i>
              </div>
              <div className="sidebar-item-text">Sticker</div>
            </div>*/}
            
            {/*<div
              className={`sidebar-item ${activeSidebarItem === 'assets' ? 'active' : ''}`}
              onClick={() => {
                setActiveSidebarItem('assets');
                setSidebarExpanded(activeSidebarItem !== 'assets' || !sidebarExpanded);
              }}
            >
              <div className="sidebar-item-icon">
                <i className="bi bi-star"></i>
              </div>
              <div className="sidebar-item-text">Assets</div>
            </div>*/}
            
            {/*<div
              className={`sidebar-item ${activeSidebarItem === 'keyboard' ? 'active' : ''}`}
              onClick={() => {
                setActiveSidebarItem('keyboard');
                setSidebarExpanded(activeSidebarItem !== 'keyboard' || !sidebarExpanded);
              }}
            >
              <div className="sidebar-item-icon">
                <i className="bi bi-keyboard"></i>
              </div>
              <div className="sidebar-item-text"></div>
            </div>*/}
          </div>
          
          <div className={`sidebar-expanded ${sidebarExpanded ? 'open' : ''}`}>
            {activeSidebarItem === 'interactive' && (
              <>
                <div className="sidebar-expanded-header">
                  <div className="sidebar-expanded-title">Interactive Components</div>
                  <div className="sidebar-expanded-close" onClick={() => setSidebarExpanded(false)}>
                    <i className="bi bi-x-lg"></i>
                  </div>
                </div>
                
                <div className="sidebar-components">
                  <div
                    className="component-item"
                    draggable="true"
                    onDragStart={() => setDraggedComponent('button')}
                    onDragEnd={() => setDraggedComponent(null)}
                  >
                    <div className="component-icon">
                      <i className="bi bi-hand-index-thumb"></i>
                    </div>
                    <div className="component-label">Button</div>
                  </div>
                  
                  <div
                    className="component-item"
                    draggable="true"
                    onDragStart={() => setDraggedComponent('link')}
                    onDragEnd={() => setDraggedComponent(null)}
                  >
                    <div className="component-icon">
                      <i className="bi bi-link-45deg"></i>
                    </div>
                    <div className="component-label">Link</div>
                  </div>
                  
                  <div
                    className="component-item"
                    draggable="true"
                    onDragStart={() => setDraggedComponent('poll')}
                    onDragEnd={() => setDraggedComponent(null)}
                  >
                    <div className="component-icon">
                      <i className="bi bi-bar-chart"></i>
                    </div>
                    <div className="component-label">Poll</div>
                  </div>
                  
                  <div
                    className="component-item"
                    draggable="true"
                    onDragStart={() => setDraggedComponent('quiz')}
                    onDragEnd={() => setDraggedComponent(null)}
                  >
                    <div className="component-icon">
                      <i className="bi bi-question-circle"></i>
                    </div>
                    <div className="component-label">Quiz</div>
                  </div>
                  
                  <div
                    className="component-item"
                    draggable="true"
                    onDragStart={() => setDraggedComponent('form')}
                    onDragEnd={() => setDraggedComponent(null)}
                  >
                    <div className="component-icon">
                      <i className="bi bi-input-cursor-text"></i>
                    </div>
                    <div className="component-label">Form</div>
                  </div>
                  
                  <div
                    className="component-item"
                    draggable="true"
                    onDragStart={() => setDraggedComponent('countdown')}
                    onDragEnd={() => setDraggedComponent(null)}
                  >
                    <div className="component-icon">
                      <i className="bi bi-clock"></i>
                    </div>
                    <div className="component-label">Countdown</div>
                  </div>
                </div>
              </>
            )}
            
            {activeSidebarItem === 'text' && (
              <>
                <div className="sidebar-expanded-header">
                  <div className="sidebar-expanded-title">Text Components</div>
                  <div className="sidebar-expanded-close" onClick={() => setSidebarExpanded(false)}>
                    <i className="bi bi-x-lg"></i>
                  </div>
                </div>
                
                <div className="sidebar-components">
                  <div
                    className="component-item"
                    draggable="true"
                    onDragStart={() => setDraggedComponent('heading')}
                    onDragEnd={() => setDraggedComponent(null)}
                  >
                    <div className="component-icon">
                      <i className="bi bi-type-h1"></i>
                    </div>
                    <div className="component-label">Heading</div>
                  </div>
                  
                  <div
                    className="component-item"
                    draggable="true"
                    onDragStart={() => setDraggedComponent('paragraph')}
                    onDragEnd={() => setDraggedComponent(null)}
                  >
                    <div className="component-icon">
                      <i className="bi bi-paragraph"></i>
                    </div>
                    <div className="component-label">Paragraph</div>
                  </div>
                </div>
              </>
            )}
            
            {activeSidebarItem === 'cta' && (
              <>
                <div className="sidebar-expanded-header">
                  <div className="sidebar-expanded-title">Call to Action</div>
                  <div className="sidebar-expanded-close" onClick={() => setSidebarExpanded(false)}>
                    <i className="bi bi-x-lg"></i>
                  </div>
                </div>
                
                <div className="sidebar-components">
                  <div
                    className="component-item"
                    draggable="true"
                    onDragStart={() => setDraggedComponent('cta-button')}
                    onDragEnd={() => setDraggedComponent(null)}
                  >
                    <div className="component-icon">
                      <i className="bi bi-cursor"></i>
                    </div>
                    <div className="component-label">CTA Button</div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="device-frame">
        {studioView === 'options' && (
          <div className="studio-options">
            <div className="option-button" onClick={() => handleOptionSelect('image')}>
              <div className="option-icon">
                <i className="bi bi-image"></i>
              </div>
              <div className="option-label">Upload Image</div>
            </div>
            <div className="option-button" onClick={() => handleOptionSelect('video')}>
              <div className="option-icon">
                <i className="bi bi-camera-video"></i>
              </div>
              <div className="option-label">Upload Video</div>
            </div>
            <div className="option-button" onClick={() => handleOptionSelect('blank')}>
              <div className="option-icon">
                <i className="bi bi-plus-circle"></i>
              </div>
              <div className="option-label">Create Blank Story</div>
            </div>
          </div>
        )}

        {studioView === 'upload' && (
          <div className="studio-upload">
            {!mediaFile ? (
              <>
                <h3>Upload {mediaType === 'PHOTO' ? 'Image' : 'Video'}</h3>
                <Form.Group className="mb-3">
                  <div
                    className={`file-drop-area ${isDragging ? 'dragging' : ''} ${mediaFile ? 'file-selected' : ''}`}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="file-drop-icon">
                      <i className="bi bi-plus-circle"></i>
                    </div>
                    <div className="file-drop-message">
                      Drag & drop your {mediaType.toLowerCase()} here
                    </div>
                    <div className="file-input-container">
                      <Form.Control
                        type="file"
                        accept={mediaType === 'PHOTO' ? 'image/*' : 'video/*'}
                        onChange={handleMediaChange}
                        className="file-input"
                        id="fileInput"
                      />
                      <Button
                        variant="primary"
                        size="sm"
                        className="browse-button"
                      >
                        Select File
                      </Button>
                    </div>
                    <div className="file-format-hint">
                      {mediaType === 'PHOTO'
                        ? 'Supported formats: JPG, PNG, GIF (max 10MB)'
                        : 'Supported formats: MP4, MOV, WebM (max 10MB)'}
                    </div>
                  </div>
                </Form.Group>
              </>
            ) : (
              <>
                {mediaType === 'PHOTO' && !showCropModal && (
                  <div className="media-preview-container">
                    <img
                      src={mediaPreview}
                      className="media-preview"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        position: 'absolute',
                        top: 0,
                        left: 0
                      }}
                      alt="Preview"
                    />
                  </div>
                )}

                {mediaType === 'VIDEO' && (
                  <div className="media-preview-container">
                    <video
                      controls
                      className="video-preview"
                      width="100%"
                      height="100%"
                      style={{
                        objectFit: 'cover',
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        top: 0,
                        left: 0
                      }}
                    >
                      <source src={mediaPreview} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                )}
              </>
            )}


          </div>
        )}

        {studioView === 'blank' && (
          <div className="studio-blank">
            <h3>Create Blank Story</h3>
            <p>This feature is coming soon.</p>
            <Button 
              variant="secondary" 
              onClick={() => setStudioView('options')}
            >
              Back
            </Button>
          </div>
        )}

        {showCropModal && (
          <div className="crop-modal">
            <div className="crop-modal-header">
              <h3>Crop Image (Optional)</h3>
              <Button variant="link" onClick={() => setShowCropModal(false)}>
                <i className="bi bi-x-lg"></i>
              </Button>
            </div>
            <div className="crop-modal-body">
              <p className="text-muted mb-3">
                Crop your image to 9:16 aspect ratio (1080x1920) for optimal display.
                This step is optional - you can skip it if you prefer to use the original image.
              </p>
              <div className="text-center">
                <p className="text-muted mb-3">Drag to position and use the slider to zoom (9:16 aspect ratio):</p>
                
                {/* Cropper container */}
                <div style={{ position: 'relative', height: '400px', width: '100%' }}>
                  <Cropper
                    image={mediaPreview}
                    crop={crop}
                    zoom={zoom}
                    aspect={9 / 16}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                  />
                </div>
                
                {/* Zoom control */}
                <div className="mt-3 mb-3" style={{ width: '100%', maxWidth: '500px', margin: '0 auto' }}>
                  <label htmlFor="zoom-slider" className="form-label">Zoom: {zoom.toFixed(1)}x</label>
                  <input
                    type="range"
                    className="form-range"
                    id="zoom-slider"
                    min="1"
                    max="3"
                    step="0.1"
                    value={zoom}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                  />
                </div>
              </div>
            </div>
            <div className="crop-modal-footer">
              <Button variant="secondary" onClick={() => setShowCropModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={getCroppedImg}
                disabled={false} // Always enable the button
              >
                Crop Image
              </Button>
            </div>
          </div>
        )}
        </div>
        
        {/* Handle component drop on device frame */}
        <div
          className="device-frame-drop-area"
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: draggedComponent ? 50 : -1 }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            
            // Get drop position relative to device frame
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            
            // Here you would handle the dropped component
            console.log(`Dropped ${draggedComponent} at position X: ${x}, Y: ${y}`);
            
            // In a real implementation, you would add the component to the story item
            // For now, just show an alert
            alert(`Added ${draggedComponent} component to story at position (${x}, ${y})`);
          }}
        />
        
        {studioView === 'upload' && (
          <div className="studio-actions-outside">
            <Button
              variant="secondary"
              onClick={() => {
                // Clear all media-related state
                setStudioView('options');
                setMediaFile(null);
                setMediaPreview(null);
                setCroppedImage(null);
                setAddItemError('');
              }}
              className="me-2"
            >
              Back
            </Button>
            
            {mediaFile && (
              <Button
                variant="primary"
                onClick={handleAddItem}
                disabled={addItemLoading}
              >
                {addItemLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Adding...
                  </>
                ) : (
                  'Add Item'
                )}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Studio;